import type { AuditChangeRow, AuditLogItem, AuditValueNode } from '@/types/Models/AuditLog'
import type { ComputedRef } from 'vue'
import { computed } from 'vue'
import {
  AUDIT_VALUE_BOOLEAN,
  AUDIT_VALUE_LIST,
  AUDIT_VALUE_NULL,
  AUDIT_VALUE_OBJECT,
  AUDIT_VALUE_SCALAR,
} from '@/const/AuditValueTypes'
import { normalizeName } from '@/services/Utils'

export const resolveValueNode = (value: unknown): AuditValueNode => {
  if (value === null || value === undefined) {
    return { type: AUDIT_VALUE_NULL, booleanValue: false, scalarValue: '', listItems: [], objectEntries: [] }
  }

  if (typeof value === 'boolean') {
    return { type: AUDIT_VALUE_BOOLEAN, booleanValue: value, scalarValue: '', listItems: [], objectEntries: [] }
  }

  if (Array.isArray(value)) {
    return { type: AUDIT_VALUE_LIST, booleanValue: false, scalarValue: '', listItems: value, objectEntries: [] }
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>).map(([key, val]) => ({
      key,
      label: normalizeName(key),
      value: val,
    }))
    return { type: AUDIT_VALUE_OBJECT, booleanValue: false, scalarValue: '', listItems: [], objectEntries: entries }
  }

  return { type: AUDIT_VALUE_SCALAR, booleanValue: false, scalarValue: String(value), listItems: [], objectEntries: [] }
}

export const useAuditLogDetails = (log: ComputedRef<AuditLogItem>) => {
  const changeRows: ComputedRef<AuditChangeRow[]> = computed(() => {
    const oldVals = log.value.oldValues ?? {}
    const newVals = log.value.newValues ?? {}
    const allKeys = new Set([...Object.keys(oldVals), ...Object.keys(newVals)])

    return Array.from(allKeys)
      .sort((a, b) => a.localeCompare(b))
      .map((key) => ({
      field: key,
      label: normalizeName(key),
      oldValue: oldVals[key] ?? null,
      newValue: newVals[key] ?? null,
    }))
  })

  const hasChanges: ComputedRef<boolean> = computed(() => changeRows.value.length > 0)

  return { changeRows, hasChanges }
}
